import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'

interface SectionHeadingProps {
    eyebrow: string
    title: string
    subtitle?: ReactNode
    as?: 'h1' | 'h2'
    size?: 'md' | 'lg' | 'xl'
    align?: 'center' | 'left'
    trigger?: 'view' | 'mount'
    className?: string
    children?: ReactNode
}

const titleSizes = {
    md: 'text-[2.15rem] sm:text-5xl md:text-[3.4rem]',
    lg: 'text-[2.5rem] sm:text-6xl md:text-[4.2rem]',
    xl: 'text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5.4rem]'
}

/** Eyebrow, large word-by-word headline and a short muted subline. Centered by default. */
export const SectionHeading = ({
    eyebrow,
    title,
    subtitle,
    as = 'h2',
    size = 'md',
    align = 'center',
    trigger = 'view',
    className,
    children
}: SectionHeadingProps) => {
    const centered = align === 'center'

    return (
        <div className={cn('flex max-w-3xl flex-col', centered ? 'mx-auto items-center text-center' : 'items-start text-left', className)}>
            <Eyebrow align={align}>
                {eyebrow}
            </Eyebrow>

            <SplitHeading
                as={as}
                text={title}
                trigger={trigger}
                className={cn('mt-6 text-balance font-normal leading-[1.02] tracking-[-0.045em] text-cream', titleSizes[size])}
            />

            {subtitle && (
                <Reveal as='p' delay={350} className='mt-6 max-w-xl text-pretty text-[15px] leading-relaxed text-cream/55 sm:text-base'>
                    {subtitle}
                </Reveal>
            )}

            {children}
        </div>
    )
}
